// Header, reading tips and the trust note shared by कथाएँ (/stories) and बातचीत (/stories/baatcheet).

import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import {
    ArrowLeft,
    Languages,
    ListOrdered,
    MousePointerClick,
    ShieldCheck
} from 'lucide-react'
import { Bi, Eyebrow } from '@/prakrit/components/common/bi'
import { ReviewerOnly, StatusBadge } from '@/prakrit/components/common/status-badge'
import { hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'

interface Text {
    hi: string
    en: string
}

interface HubHeaderProps {
    eyebrow: string
    title: Text
    intro: Text
    kidsIntro: Text
    counts: { n: number, hi: string, en: string }[]
    back?: { href: string, hi: string, en: string }
}

export const HubHeader: FC<HubHeaderProps> = ({ eyebrow, title, intro, kidsIntro, counts, back }): ReactNode => (
    <header className='flex flex-col gap-5 pb-8 pt-8 sm:pb-10 sm:pt-12'>
        {back && (
            <nav aria-label='Breadcrumb'>
                <Link
                    href={back.href}
                    className='group inline-flex min-h-tap items-center gap-2 rounded-full border border-border bg-card/80 px-4 text-sm font-medium transition-colors hover:border-saffron/60 hover:text-prakrit'
                >
                    <ArrowLeft className='size-4 transition-transform group-hover:-translate-x-0.5' aria-hidden='true' />
                    <span lang='hi' className='hindi'>{back.hi}</span>
                    <span aria-hidden='true' className='text-muted-foreground'>·</span>
                    <span lang='en'>{back.en}</span>
                </Link>
            </nav>
        )}
        <div className='flex max-w-3xl flex-col gap-4'>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h1 className='flex flex-col gap-1.5'>
                <span lang='hi' className='text-[2.25rem] font-bold leading-tight sm:text-[3rem]'>{title.hi}</span>
                <span lang='en' className='font-sans text-xl font-medium text-muted-foreground sm:text-2xl'>{title.en}</span>
            </h1>
            <Bi hi={intro.hi} en={intro.en} className='adults-only gap-1.5 text-base leading-relaxed sm:text-[1.0625rem]' />
            <Bi hi={kidsIntro.hi} en={kidsIntro.en} className='kids-only gap-1.5 text-base leading-relaxed sm:text-[1.0625rem]' />
            {counts.length > 0 && (
                <ul className='flex flex-wrap gap-2 pt-1 text-sm'>
                    {counts.map(c => (
                        <li key={c.en} className='inline-flex items-baseline gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5'>
                            <span lang='hi' className='hindi font-semibold text-prakrit'>{hiDigits(c.n)}</span>
                            <span lang='hi' className='hindi'>{c.hi}</span>
                            <span aria-hidden='true' className='text-muted-foreground'>·</span>
                            <span lang='en' className='text-muted-foreground'>{c.n} {c.en}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    </header>
)

const TIPS: { icon: LucideIcon, hi: string, en: string, body: Text }[] = [
    {
        icon: MousePointerClick,
        hi: 'हर शब्द छुएँ',
        en: 'Tap any word',
        body: { hi: 'प्राकृत के किसी भी शब्द पर टैप करें — उसका अर्थ, रूप और तालिका खुलती है।', en: 'Tap any Prakrit word to see its meaning, its form and its table.' }
    },
    {
        icon: Languages,
        hi: 'अनुवाद जब चाहें',
        en: 'Translation when you want it',
        body: { hi: 'पहले स्वयं पढ़ने की कोशिश करें, फिर हर वाक्य का हिन्दी और अंग्रेज़ी अनुवाद खोलें।', en: 'Try to read first, then open the Hindi and English of each sentence.' }
    },
    {
        icon: ListOrdered,
        hi: 'प्रश्न और क्रम',
        en: 'Questions and order',
        body: { hi: 'अंत में तीन प्रश्न, वाक्यों को सही क्रम में लगाना और नए शब्दों की सूची।', en: 'Each ends with three questions, putting the lines in order and a list of new words.' }
    }
]

export const ReadingTips: FC = (): ReactNode => (
    <section aria-label='कैसे पढ़ें · How to read' className='grid gap-3 sm:grid-cols-3'>
        {TIPS.map(tip => {
            const Icon = tip.icon

            return (
                <div key={tip.en} className='flex gap-4 rounded-xl border border-border bg-surface-muted/60 px-4 py-4'>
                    <span aria-hidden='true' className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-card text-saffron-strong shadow-card'>
                        <Icon className='size-5' />
                    </span>
                    <span className='flex min-w-0 flex-col gap-1'>
                        <span className='flex flex-wrap items-baseline gap-x-2'>
                            <span lang='hi' className='hindi font-semibold'>{tip.hi}</span>
                            <span lang='en' className='text-sm text-muted-foreground'>{tip.en}</span>
                        </span>
                        <Bi hi={tip.body.hi} en={tip.body.en} className='gap-0.5 text-sm leading-relaxed' />
                    </span>
                </div>
            )
        })}
    </section>
)

export const StoriesTrustNote: FC = (): ReactNode => (
    <aside className='mb-16 mt-14 flex flex-col gap-3 rounded-xl border border-border bg-card px-5 py-4 shadow-card sm:flex-row sm:items-center sm:gap-5'>
        <ShieldCheck className='size-6 shrink-0 text-saffron-strong' aria-hidden='true' />
        <p className='flex-1 text-sm leading-relaxed'>
            <span lang='hi' data-ex='hi' className='hindi block'>
                ये कथाएँ नई लिखी गई हैं और केवल उन्हीं रूपों से बनी हैं जो उस इकाई तक सिखाए जा चुके हैं। जो रूप बाद में सिखाया जाता है, उसे वाक्यांश के रूप में पहले समझा दिया गया है।
                <ReviewerOnly>{' '}हर कथा शिक्षक की जाँच के बाद ही खुलती है; तब तक उस पर यह चिह्न रहता है:</ReviewerOnly>
            </span>
            <span lang='en' data-ex='en' className='block text-muted-foreground'>
                These stories are newly written and use only forms taught by their unit. A form taught later is explained first as a phrase.
                <ReviewerOnly>{' '}Each story opens only after a teacher has checked it; until then it carries this badge:</ReviewerOnly>
            </span>
        </p>
        <StatusBadge kind='needs_review' className='self-start sm:self-center' />
    </aside>
)
